// Extracted Post Studio view components from SocialStudio.jsx
// (Round 5A/5B/5C: PostRefineView, CarouselView, ThreadView)
import React from 'react';
import { toast } from 'sonner';
import {
  Camera, Copy, Trash2, Sparkles, RefreshCw, Save, TrendingUp, Wand2,
  ShieldAlert, ShieldCheck, Heart, Zap, Gauge, Pencil, Download,
} from 'lucide-react';
import { toTeleprompterText, downloadTxt } from '../../lib/teleprompterTxt';

const scoreColor = (s) => (s >= 85 ? '#059669' : s >= 70 ? '#1D4ED8' : s >= 50 ? '#D97706' : '#DC2626');

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">{label}</div>
      <div className="text-xs text-[#0A0F1C]">{value}</div>
    </div>
  );
}

// ===== Round 5A: Post Refine View =====
function PostRefineView({
  post, onCopy, onSelectVariant,
  onHumanize, humanizeLevel, setHumanizeLevel,
  onMoreViral, onMoreEmotional, onShorter, onLonger, onCompliant,
  onRewrite, rewriteFeedback, setRewriteFeedback,
  transforming, onStatusChange, onDelete,
  // Round 5C
  imageStyle, setImageStyle, imageAspect, setImageAspect,
  imageCustomPrompt, setImageCustomPrompt, imageGenLoading,
  onGenerateImage, onDeleteImage, onHostImage,
  imageSuggestions, imageSuggestionsLoading, onSuggestImages,
  scheduleDate, setScheduleDate, scheduleTime, setScheduleTime,
  scheduleChannel, setScheduleChannel,
  scheduleAutoPublish, setScheduleAutoPublish,
  schedulingLoading, onSchedule, onCancelSchedule,
  convertTarget, setConvertTarget, convertGoal, setConvertGoal,
  convertLoading, onConvertToVideo,
  complianceLoading, onComplianceRecheck,
  schedulerProfiles,
  onGeneratePlatformHashtags, generatingPlatformHashtags,
}) {
  const idx = post.selected_variant_idx || 0;
  const v = (post.variants || [])[idx] || {};
  const vs = v.viral_scores || {};
  const cmp = v.compliance || {};
  const img = v.image_suggestion || {};
  const images = v.images || [];
  const overall = Number(vs.overall || 0);
  const activeScheduleId = post.active_schedule_id;
  const scheduledFor = post.scheduled_for;

  return (
    <div className="grid lg:grid-cols-3 gap-4" data-testid="post-refine">
      <div className="lg:col-span-2 space-y-3">
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#94A3B8]">{post.platform} · {post.post_type} · {v.chosen_framework || post.framework}</div>
              <h2 className="font-bold text-[#0A0F1C]">{post.topic}</h2>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap" data-testid="post-variants">
              {(post.variants || []).map((vv, i) => (
                <button key={vv.version_label || `variant-${i}`} type="button"
                  onClick={() => onSelectVariant(i)}
                  aria-pressed={i === idx}
                  data-testid={`post-variant-${i}`}
                  className={`px-2 py-0.5 text-[10px] font-semibold rounded-full border ${i === idx ? 'bg-[#0A0F1C] text-white border-[#0A0F1C]' : 'bg-white text-[#475569] border-[#E2E8F0]'}`}
                  title={vv.version_label || `Variant ${i + 1}`}>
                  {i + 1}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-4 whitespace-pre-wrap text-sm text-[#0A0F1C] leading-relaxed" data-testid="post-full">
            {v.full_post || '(no body)'}
          </div>

          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => onCopy(v.full_post || '')} data-testid="copy-post"
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-[#F1F5F9] text-[#0A0F1C] rounded-lg">
              <Copy size={12} aria-hidden="true" /> Copy post
            </button>
            <button type="button" onClick={() => onCopy((v.hashtags || []).join(' '))} data-testid="copy-hashtags"
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-[#F1F5F9] text-[#0A0F1C] rounded-lg">
              <Copy size={12} aria-hidden="true" /> Copy hashtags
            </button>
            <button type="button" onClick={() => { downloadTxt(post.topic || 'post', toTeleprompterText(v.full_post || '')); toast.success('Teleprompter .txt downloaded'); }}
              data-testid="download-post-txt" disabled={!v.full_post}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-[#FFF7ED] text-[#C2410C] rounded-lg disabled:opacity-50">
              <Download size={12} aria-hidden="true" /> Download .txt
            </button>
            <button type="button" onClick={() => onStatusChange(post.post_id, 'scheduled')} data-testid="mark-post-scheduled"
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-amber-50 text-amber-700 rounded-lg">
              <Save size={12} aria-hidden="true" /> Mark scheduled
            </button>
            <button type="button" onClick={() => onStatusChange(post.post_id, 'posted')} data-testid="mark-post-posted"
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-emerald-50 text-emerald-700 rounded-lg">
              <Save size={12} aria-hidden="true" /> Mark posted
            </button>
            <button type="button" onClick={() => onDelete(post.post_id)} data-testid="delete-post"
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-red-50 text-red-700 rounded-lg ml-auto">
              <Trash2 size={12} aria-hidden="true" /> Delete
            </button>
          </div>

          {v.short_version && (
            <div className="pt-2 border-t border-[#F1F5F9]">
              <div className="flex items-center justify-between"><div className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">Short version</div><button type="button" onClick={() => onCopy(v.short_version)} aria-label="Copy short" className="text-[#64748B] hover:text-[#1D4ED8]"><Copy size={11} aria-hidden="true" /></button></div>
              <p className="text-xs text-[#475569] whitespace-pre-wrap mt-1">{v.short_version}</p>
            </div>
          )}
          {v.expanded_version && (
            <div className="pt-2 border-t border-[#F1F5F9]">
              <div className="flex items-center justify-between"><div className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">Expanded version</div><button type="button" onClick={() => onCopy(v.expanded_version)} aria-label="Copy expanded" className="text-[#64748B] hover:text-[#1D4ED8]"><Copy size={11} aria-hidden="true" /></button></div>
              <p className="text-xs text-[#475569] whitespace-pre-wrap mt-1">{v.expanded_version}</p>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-2 pt-2 border-t border-[#F1F5F9]">
            {v.cta && <Field label="CTA" value={v.cta} />}
            {v.engagement_question && <Field label="Engagement question" value={v.engagement_question} />}
            {v.pinned_comment && <Field label="Pinned comment" value={v.pinned_comment} />}
            {v.hashtags?.length > 0 && (
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">Hashtags</div>
                <div className="flex flex-wrap gap-1 mt-0.5">
                  {v.hashtags.map((h) => <span key={h} className="text-[10px] px-2 py-0.5 bg-[#EFF6FF] text-[#1D4ED8] rounded-full">{h}</span>)}
                </div>
              </div>
            )}
          </div>

          {/* Recommended hashtags per platform (auto-generated on post creation) */}
          {(v.platform_hashtags || onGeneratePlatformHashtags) && (
            <div className="pt-2 border-t border-[#F1F5F9]" data-testid="post-platform-hashtags">
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">Recommended hashtags by platform</div>
                {!v.platform_hashtags && onGeneratePlatformHashtags && (
                  <button type="button" onClick={onGeneratePlatformHashtags} disabled={generatingPlatformHashtags}
                    data-testid="generate-post-platform-hashtags"
                    className="text-[11px] font-bold text-[#1D4ED8] hover:underline disabled:opacity-50">
                    {generatingPlatformHashtags ? 'Generating…' : 'Suggest per-platform hashtags'}
                  </button>
                )}
              </div>
              {v.platform_hashtags && (
                <div className="grid sm:grid-cols-2 gap-2">
                  {[['facebook', 'Facebook'], ['instagram', 'Instagram'], ['linkedin', 'LinkedIn'], ['x_twitter', 'X / Twitter'], ['tiktok', 'TikTok']].map(([k, lbl]) => {
                    const tags = v.platform_hashtags[k] || [];
                    if (!tags.length) return null;
                    return (
                      <div key={k} className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-2.5" data-testid={`post-platform-hashtags-${k}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">{lbl}</span>
                          <button type="button" onClick={() => onCopy(tags.join(' '))} data-testid={`post-copy-hashtags-${k}`}
                            aria-label={`Copy ${lbl} hashtags`} className="text-[10px] font-bold text-[#1D4ED8] hover:underline">Copy #tags</button>
                        </div>
                        <div className="text-[11px] text-[#1D4ED8] break-words">{tags.join(' ')}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Transformations */}
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5 space-y-3" data-testid="post-transformations">
          <div className="flex items-center gap-2">
            <Zap size={14} className="text-[#1D4ED8]" aria-hidden="true" />
            <h2 className="text-sm font-bold uppercase tracking-wider text-[#0A0F1C]">Transformations</h2>
            {transforming && <RefreshCw size={11} className="animate-spin text-[#94A3B8]" aria-hidden="true" />}
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1">Humanize level</div>
            <div className="flex flex-wrap gap-1.5">
              {[1, 2, 3, 4].map((lvl) => (
                <button key={lvl} type="button" onClick={() => setHumanizeLevel(lvl)}
                  data-testid={`post-humanize-level-${lvl}`}
                  aria-pressed={humanizeLevel === lvl}
                  className={`px-2.5 py-1 text-[11px] font-semibold rounded-full border ${humanizeLevel === lvl ? 'bg-[#0A0F1C] text-white border-[#0A0F1C]' : 'bg-white text-[#475569] border-[#E2E8F0]'}`}>
                  L{lvl} {['Slightly', 'Conversational', 'Natural', 'Raw'][lvl - 1]}
                </button>
              ))}
              <button type="button" onClick={onHumanize} disabled={transforming}
                data-testid="post-humanize-apply"
                className="ml-1 inline-flex items-center gap-1 px-3 py-1 text-[11px] font-bold rounded-full bg-[#1D4ED8] text-white disabled:opacity-50">
                <Heart size={11} aria-hidden="true" /> Humanize
              </button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={onMoreViral} disabled={transforming} data-testid="post-more-viral"
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200 rounded-lg disabled:opacity-50">
              <TrendingUp size={12} aria-hidden="true" /> More viral
            </button>
            <button type="button" onClick={onMoreEmotional} disabled={transforming} data-testid="post-more-emotional"
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200 rounded-lg disabled:opacity-50">
              <Heart size={12} aria-hidden="true" /> More emotional
            </button>
            <button type="button" onClick={onShorter} disabled={transforming} data-testid="post-shorter"
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-[#F1F5F9] text-[#0A0F1C] rounded-lg disabled:opacity-50">
              ↓ Shorter
            </button>
            <button type="button" onClick={onLonger} disabled={transforming} data-testid="post-longer"
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-[#F1F5F9] text-[#0A0F1C] rounded-lg disabled:opacity-50">
              ↑ Longer
            </button>
            <button type="button" onClick={onCompliant} disabled={transforming} data-testid="post-compliant"
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg disabled:opacity-50">
              <ShieldCheck size={12} aria-hidden="true" /> Make compliant
            </button>
          </div>
          <div className="flex gap-2">
            <input type="text" placeholder="Rewrite feedback (e.g. 'add a specific number, cut the second paragraph')"
              aria-label="Rewrite feedback"
              data-testid="post-rewrite-feedback"
              value={rewriteFeedback}
              onChange={(e) => setRewriteFeedback(e.target.value)}
              className="flex-1 px-3 py-1.5 border border-[#E2E8F0] rounded-lg text-xs" />
            <button type="button" onClick={onRewrite} disabled={transforming || !rewriteFeedback.trim()}
              data-testid="post-rewrite"
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-[#1D4ED8] text-white rounded-lg disabled:opacity-50">
              <Pencil size={12} aria-hidden="true" /> Rewrite
            </button>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {/* Viral score */}
        {Object.keys(vs).length > 0 && (
          <div className="bg-white border border-[#E2E8F0] rounded-2xl p-4 space-y-2" data-testid="post-viral-score">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Gauge size={14} className="text-[#1D4ED8]" aria-hidden="true" />
                <h2 className="text-sm font-bold uppercase tracking-wider text-[#0A0F1C]">Viral score</h2>
              </div>
              <span className="text-3xl font-black" style={{ color: scoreColor(overall) }}>{overall}</span>
            </div>
            <div className="space-y-1">
              {[
                ['hook_strength', 'Hook'],
                ['shareability', 'Share'],
                ['emotional_impact', 'Emotion'],
                ['comment_potential', 'Comments'],
                ['save_potential', 'Saves'],
                ['virality', 'Virality'],
                ['clarity', 'Clarity'],
                ['platform_fit', 'Platform fit'],
              ].map(([k, label]) => {
                const val = Number(vs[k] || 0);
                return (
                  <div key={k}>
                    <div className="flex justify-between text-[10px]"><span className="text-[#475569]">{label}</span><span className="font-bold" style={{ color: scoreColor(val) }}>{val}</span></div>
                    <div className="h-1 bg-[#F1F5F9] rounded-full overflow-hidden"><div className="h-full rounded-full" style={{ width: `${val}%`, backgroundColor: scoreColor(val) }} /></div>
                  </div>
                );
              })}
            </div>
            {vs.explanation?.what_is_working && <p className="text-[10px] text-emerald-700"><strong>Working:</strong> {vs.explanation.what_is_working}</p>}
            {vs.explanation?.weakest_area && <p className="text-[10px] text-amber-700"><strong>Weak:</strong> {vs.explanation.weakest_area}</p>}
          </div>
        )}

        {/* Emotional score */}
        {v.emotional_score && (
          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-4" data-testid="post-emotional-score">
            <div className="flex items-center justify-between">
              <div className="text-[10px] font-bold uppercase tracking-wider text-rose-700">Emotional ({v.emotional_score.target})</div>
              <span className="text-xl font-black" style={{ color: scoreColor(Number(v.emotional_score.score || 0)) }}>{Number(v.emotional_score.score || 0)}</span>
            </div>
            <p className="text-[10px] text-[#475569] mt-1">{v.emotional_score.why}</p>
          </div>
        )}

        {/* Engagement prediction */}
        {v.engagement_prediction && (
          <div className="bg-[#EFF6FF] border border-[#DBEAFE] rounded-2xl p-3 text-center" data-testid="post-engagement-prediction">
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#1D4ED8]">Engagement prediction</div>
            <div className="text-xl font-black text-[#1D4ED8] capitalize">{v.engagement_prediction}</div>
          </div>
        )}

        {/* Compliance */}
        {Object.keys(cmp).length > 0 && (
          <div className={`border rounded-2xl p-4 space-y-1 ${cmp.risk_level === 'high' ? 'bg-red-50 border-red-200' : cmp.risk_level === 'medium' ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'}`} data-testid="post-compliance">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <ShieldAlert size={14} aria-hidden="true" />
                <h2 className="text-xs font-bold uppercase tracking-wider">Compliance · {cmp.risk_level} risk</h2>
              </div>
              <button type="button" onClick={onComplianceRecheck} disabled={complianceLoading}
                data-testid="post-compliance-recheck"
                className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold bg-white border border-[#E2E8F0] text-[#0A0F1C] rounded-full disabled:opacity-50">
                {complianceLoading ? <RefreshCw size={10} className="animate-spin" aria-hidden="true" /> : <RefreshCw size={10} aria-hidden="true" />} Recheck
              </button>
            </div>
            {cmp.flagged_phrases?.length > 0 && (
              <div><div className="text-[10px] font-bold text-[#475569]">Flagged</div>
                <div className="flex flex-wrap gap-1">{cmp.flagged_phrases.map((p, i) => <span key={`flag-${i}-${(p || '').slice(0, 16)}`} className="text-[10px] bg-white border border-red-200 text-red-700 px-1.5 py-0.5 rounded-full">{p}</span>)}</div></div>
            )}
            {cmp.safer_rewrites?.length > 0 && (
              <div><div className="text-[10px] font-bold text-[#475569] mt-1">Safer rewrites</div>
                <ul className="space-y-0.5">{cmp.safer_rewrites.map((r, i) => <li key={`safer-${i}-${(r || '').slice(0, 16)}`} className="text-[10px] bg-white border border-[#E2E8F0] rounded-lg p-1">{r}</li>)}</ul></div>
            )}
            {cmp.suggested_disclaimer && <p className="text-[10px] text-[#0A0F1C] bg-white border border-[#E2E8F0] rounded-lg p-1.5">{cmp.suggested_disclaimer}</p>}
          </div>
        )}

        {/* Image suggestion */}
        {(img.concept || img.ai_prompt) && (
          <div className="bg-white border border-[#E2E8F0] rounded-2xl p-4 space-y-2" data-testid="post-image-suggestion">
            <div className="flex items-center gap-2">
              <Camera size={14} className="text-[#1D4ED8]" aria-hidden="true" />
              <h2 className="text-sm font-bold uppercase tracking-wider text-[#0A0F1C]">Image suggestion</h2>
            </div>
            {img.concept && <div><div className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">Concept</div><div className="text-xs text-[#0A0F1C]">{img.concept}</div></div>}
            {img.mood && <div className="text-[10px] text-[#475569]"><strong>Mood:</strong> {img.mood}</div>}
            {img.camera_angle && <div className="text-[10px] text-[#475569]"><strong>Angle:</strong> {img.camera_angle}</div>}
            {img.background && <div className="text-[10px] text-[#475569]"><strong>Background:</strong> {img.background}</div>}
            {img.stock_photo_search && (
              <div className="text-[10px] text-[#475569] flex items-center gap-2">
                <span><strong>Stock keywords:</strong> {img.stock_photo_search}</span>
                <button type="button" onClick={() => onCopy(img.stock_photo_search)} aria-label="Copy stock keywords" className="text-[#64748B] hover:text-[#1D4ED8]"><Copy size={10} aria-hidden="true" /></button>
              </div>
            )}
            {img.ai_prompt && (
              <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-2" data-testid="post-image-ai-prompt">
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">AI image prompt</span>
                  <button type="button" onClick={() => onCopy(img.ai_prompt)} data-testid="copy-ai-prompt"
                    className="text-[#64748B] hover:text-[#1D4ED8]"><Copy size={10} aria-hidden="true" /></button>
                </div>
                <p className="text-[10px] text-[#0A0F1C]">{img.ai_prompt}</p>
              </div>
            )}
          </div>
        )}

        {/* ===== Round 5C: AI image generator (Gemini Nano Banana) ===== */}
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-4 space-y-2" data-testid="post-image-generator">
          <div className="flex items-center gap-2">
            <Sparkles size={14} className="text-fuchsia-600" aria-hidden="true" />
            <h2 className="text-sm font-bold uppercase tracking-wider text-[#0A0F1C]">AI image (Nano Banana)</h2>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <select value={imageStyle} onChange={(e) => setImageStyle(e.target.value)} aria-label="Image style"
              data-testid="image-style-select"
              className="text-xs px-2 py-1.5 border border-[#E2E8F0] rounded-lg">
              <option value="photorealistic">Photorealistic</option>
              <option value="illustration">Illustration</option>
              <option value="bold-graphic">Bold graphic</option>
              <option value="minimal">Minimal</option>
            </select>
            <select value={imageAspect} onChange={(e) => setImageAspect(e.target.value)} aria-label="Image aspect"
              data-testid="image-aspect-select"
              className="text-xs px-2 py-1.5 border border-[#E2E8F0] rounded-lg">
              <option value="square">Square 1:1</option>
              <option value="portrait">Portrait 4:5</option>
              <option value="landscape">Landscape 16:9</option>
            </select>
          </div>
          <textarea rows={2} value={imageCustomPrompt} onChange={(e) => setImageCustomPrompt(e.target.value)}
            placeholder="Optional: custom prompt (leave blank to use suggestion above)"
            aria-label="Custom image prompt"
            data-testid="image-custom-prompt"
            className="w-full text-xs px-2 py-1.5 border border-[#E2E8F0] rounded-lg" />
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={onGenerateImage} disabled={imageGenLoading}
              data-testid="generate-image-button"
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-fuchsia-600 text-white rounded-lg disabled:opacity-50">
              {imageGenLoading ? <RefreshCw size={11} className="animate-spin" aria-hidden="true" /> : <Sparkles size={11} aria-hidden="true" />}
              {imageGenLoading ? 'Generating...' : 'Generate image'}
            </button>
            <button type="button" onClick={onSuggestImages} disabled={imageSuggestionsLoading}
              data-testid="suggest-images-button"
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-white border border-[#E2E8F0] text-[#0A0F1C] rounded-lg disabled:opacity-50">
              {imageSuggestionsLoading ? <RefreshCw size={11} className="animate-spin" aria-hidden="true" /> : <Wand2 size={11} aria-hidden="true" />}
              Suggest alternates
            </button>
          </div>
          {images.length > 0 && (
            <div className="grid grid-cols-2 gap-2 pt-2" data-testid="post-images-grid">
              {images.map((im) => (
                <div key={im.image_id} className="relative group">
                  <img src={`data:${im.mime_type};base64,${im.data_base64}`} alt="" className="w-full rounded-lg border border-[#E2E8F0]" />
                  <div className="absolute top-1 right-1 flex flex-col gap-1">
                    {onHostImage && (
                      <button type="button" onClick={() => onHostImage(im.image_id)} aria-label="Host for IG"
                        data-testid={`host-image-${im.image_id}`}
                        title="Host this image publicly (for Instagram auto-posting)"
                        className="bg-white/90 hover:bg-white p-1 rounded-full">
                        <Sparkles size={10} className="text-emerald-600" aria-hidden="true" />
                      </button>
                    )}
                    <button type="button" onClick={() => onDeleteImage(im.image_id)} aria-label="Delete image"
                      data-testid={`delete-image-${im.image_id}`}
                      className="bg-white/90 hover:bg-white p-1 rounded-full">
                      <Trash2 size={10} className="text-red-600" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {imageSuggestions.length > 0 && (
            <div className="space-y-1 pt-2 border-t border-[#F1F5F9]" data-testid="image-suggestions-list">
              <div className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">Alternate concepts</div>
              {imageSuggestions.map((sg, i) => (
                <div key={sg.concept || `sugg-${i}`} className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-2 text-[10px]">
                  <div className="font-semibold text-[#0A0F1C]">{sg.concept}</div>
                  {sg.mood && <div className="text-[#475569]"><strong>Mood:</strong> {sg.mood}</div>}
                  {sg.ai_prompt && (
                    <div className="flex items-center gap-1 mt-0.5">
                      <span className="text-[#475569] truncate">{sg.ai_prompt}</span>
                      <button type="button" onClick={() => { setImageCustomPrompt(sg.ai_prompt); toast.success('Prompt loaded'); }}
                        data-testid={`use-suggestion-${i}`}
                        className="ml-auto text-[#1D4ED8] hover:underline font-semibold whitespace-nowrap">Use</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ===== Round 5C: Schedule ===== */}
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-4 space-y-2" data-testid="post-scheduler">
          <div className="flex items-center gap-2">
            <Save size={14} className="text-amber-600" aria-hidden="true" />
            <h2 className="text-sm font-bold uppercase tracking-wider text-[#0A0F1C]">Schedule this post</h2>
          </div>
          {scheduledFor ? (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-[11px]" data-testid="active-schedule">
              <div className="font-semibold text-amber-900">Scheduled for {new Date(scheduledFor).toLocaleString()}</div>
              {activeScheduleId && (
                <button type="button" onClick={() => onCancelSchedule(activeScheduleId)}
                  data-testid="cancel-schedule-button"
                  className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold bg-white border border-amber-300 text-amber-800 rounded-full">
                  Cancel schedule
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-2">
                <input type="date" value={scheduleDate} onChange={(e) => setScheduleDate(e.target.value)}
                  aria-label="Schedule date"
                  data-testid="schedule-date"
                  className="text-xs px-2 py-1.5 border border-[#E2E8F0] rounded-lg" />
                <input type="time" value={scheduleTime} onChange={(e) => setScheduleTime(e.target.value)}
                  aria-label="Schedule time"
                  data-testid="schedule-time"
                  className="text-xs px-2 py-1.5 border border-[#E2E8F0] rounded-lg" />
              </div>
              {schedulerProfiles.length > 0 && (
                <select value={scheduleChannel} onChange={(e) => setScheduleChannel(e.target.value)}
                  aria-label="Buffer channel"
                  data-testid="schedule-channel"
                  className="w-full text-xs px-2 py-1.5 border border-[#E2E8F0] rounded-lg">
                  <option value="">Internal only (no Buffer push)</option>
                  {schedulerProfiles.map((p) => (
                    <option key={p.id} value={p.id}>{p.service}: {p.name}</option>
                  ))}
                </select>
              )}
              <label className="flex items-center gap-2 text-[11px] text-[#475569]">
                <input type="checkbox" checked={scheduleAutoPublish}
                  onChange={(e) => setScheduleAutoPublish(e.target.checked)}
                  data-testid="schedule-auto-publish" />
                Push to Buffer for auto-publishing
              </label>
              <button type="button" onClick={onSchedule} disabled={schedulingLoading || !scheduleDate}
                data-testid="schedule-button"
                className="w-full inline-flex items-center justify-center gap-1 px-3 py-2 text-xs font-semibold bg-amber-600 text-white rounded-lg disabled:opacity-50">
                {schedulingLoading ? <RefreshCw size={11} className="animate-spin" aria-hidden="true" /> : <Save size={11} aria-hidden="true" />}
                Schedule
              </button>
            </>
          )}
        </div>

        {/* ===== Round 5C: Convert to video ===== */}
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-4 space-y-2" data-testid="post-convert-to-video">
          <div className="flex items-center gap-2">
            <TrendingUp size={14} className="text-[#1D4ED8]" aria-hidden="true" />
            <h2 className="text-sm font-bold uppercase tracking-wider text-[#0A0F1C]">Convert to video script</h2>
          </div>
          <select value={convertTarget} onChange={(e) => setConvertTarget(e.target.value)}
            aria-label="Target video platform"
            data-testid="convert-target"
            className="w-full text-xs px-2 py-1.5 border border-[#E2E8F0] rounded-lg">
            <option value="tiktok">TikTok</option>
            <option value="instagram_reels">Instagram Reels</option>
            <option value="facebook_reels">Facebook Reels</option>
            <option value="youtube_shorts">YouTube Shorts</option>
            <option value="snapchat">Snapchat</option>
            <option value="x_twitter">X / Twitter Video</option>
          </select>
          <input type="text" value={convertGoal} onChange={(e) => setConvertGoal(e.target.value)}
            aria-label="Video goal"
            data-testid="convert-goal"
            placeholder="Goal e.g. drive saves and comments"
            className="w-full text-xs px-2 py-1.5 border border-[#E2E8F0] rounded-lg" />
          <button type="button" onClick={onConvertToVideo} disabled={convertLoading}
            data-testid="convert-to-video-button"
            className="w-full inline-flex items-center justify-center gap-1 px-3 py-2 text-xs font-semibold bg-[#1D4ED8] text-white rounded-lg disabled:opacity-50">
            {convertLoading ? <RefreshCw size={11} className="animate-spin" aria-hidden="true" /> : <TrendingUp size={11} aria-hidden="true" />}
            {convertLoading ? 'Converting...' : 'Generate 60s video script'}
          </button>
          {post.derived_video_script_ids?.length > 0 && (
            <div className="text-[10px] text-[#475569]">
              <strong>Linked:</strong> {post.derived_video_script_ids.length} video script(s)
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ===== Round 5B helpers =====

function CarouselView({ carousel, onCopy }) {
  const r = carousel?.result || {};
  const slides = r.slides || [];
  const overall = Number((r.viral_scores || {}).overall || 0);
  return (
    <div className="space-y-3" data-testid="carousel-view">
      <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#94A3B8]">{carousel.platform} · {carousel.carousel_type} · {slides.length} slides</div>
            <h2 className="font-bold text-[#0A0F1C]">{carousel.topic}</h2>
          </div>
          {overall > 0 && (<div className="text-right"><div className="text-2xl font-black" style={{ color: scoreColor(overall) }}>{overall}</div><div className="text-[10px] text-[#94A3B8]">viral</div></div>)}
        </div>
        {r.caption && (
          <div className="mt-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">Caption</span>
              <button type="button" onClick={() => onCopy(r.caption)} className="text-[#64748B] hover:text-[#1D4ED8]"><Copy size={11} aria-hidden="true" /></button>
            </div>
            <p className="text-xs whitespace-pre-wrap text-[#0A0F1C]">{r.caption}</p>
          </div>
        )}
        {r.hashtags?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">{r.hashtags.map((h) => <span key={h} className="text-[10px] px-2 py-0.5 bg-[#EFF6FF] text-[#1D4ED8] rounded-full">{h}</span>)}</div>
        )}
      </div>
      <div className="grid sm:grid-cols-2 gap-3" data-testid="carousel-slides">
        {slides.map((s, i) => (
          <div key={`slide-${s.index || i}`} className={`border rounded-2xl p-4 ${s.role === 'hook' ? 'bg-amber-50 border-amber-200' : s.role === 'cta' ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-[#E2E8F0]'}`} data-testid={`slide-${i + 1}`}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">Slide {s.index || i + 1} · {s.role || 'value'}</span>
              <button type="button" onClick={() => onCopy(`${s.title}\n\n${s.body}`)} className="text-[#64748B] hover:text-[#1D4ED8]"><Copy size={11} aria-hidden="true" /></button>
            </div>
            <h3 className="font-bold text-sm text-[#0A0F1C]">{s.title}</h3>
            <p className="text-xs text-[#475569] mt-1 whitespace-pre-wrap">{s.body}</p>
            {s.visual_suggestion && <p className="text-[10px] text-[#94A3B8] mt-2 italic">📷 {s.visual_suggestion}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

function ThreadView({ thread, onCopy }) {
  const r = thread?.result || {};
  const posts = r.posts || [];
  const overall = Number((r.viral_scores || {}).overall || 0);
  return (
    <div className="space-y-3" data-testid="thread-view">
      <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#94A3B8]">{thread.platform} · {thread.thread_type} · {posts.length + 2} nodes</div>
            <h2 className="font-bold text-[#0A0F1C]">{thread.topic}</h2>
          </div>
          {overall > 0 && (<div className="text-right"><div className="text-2xl font-black" style={{ color: scoreColor(overall) }}>{overall}</div><div className="text-[10px] text-[#94A3B8]">viral</div></div>)}
        </div>
      </div>
      <div className="space-y-2" data-testid="thread-posts">
        {r.hook && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4" data-testid="thread-hook">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700">1 · Hook</span>
              <button type="button" onClick={() => onCopy(r.hook)} className="text-[#64748B] hover:text-[#1D4ED8]"><Copy size={11} aria-hidden="true" /></button>
            </div>
            <p className="text-sm font-semibold text-[#0A0F1C] whitespace-pre-wrap">{r.hook}</p>
          </div>
        )}
        {posts.map((p, i) => (
          <div key={`thread-${i}-${(p || '').slice(0, 20)}`} className="bg-white border border-[#E2E8F0] rounded-2xl p-4" data-testid={`thread-node-${i + 2}`}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">{i + 2}</span>
              <button type="button" onClick={() => onCopy(p)} className="text-[#64748B] hover:text-[#1D4ED8]"><Copy size={11} aria-hidden="true" /></button>
            </div>
            <p className="text-sm text-[#0A0F1C] whitespace-pre-wrap">{p}</p>
          </div>
        ))}
        {r.cta && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4" data-testid="thread-cta">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">{posts.length + 2} · CTA</span>
              <button type="button" onClick={() => onCopy(r.cta)} className="text-[#64748B] hover:text-[#1D4ED8]"><Copy size={11} aria-hidden="true" /></button>
            </div>
            <p className="text-sm font-semibold text-[#0A0F1C] whitespace-pre-wrap">{r.cta}</p>
          </div>
        )}
        {r.engagement_question && (
          <p className="text-xs text-[#475569] italic mt-2">Reply prompt: "{r.engagement_question}"</p>
        )}
      </div>
    </div>
  );
}


export { PostRefineView, CarouselView, ThreadView };
